
import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import EvaVideo from "./EvaVideo";
import "./EvaHealthSafetyBriefing.css";


//========================================================
// Health & Safety Briefing Demonstration
//========================================================

export const HEALTH_SAFETY_TOPICS = [
    {
        id: "escape",
        title: "Obstructed Emergency Exits",
        image: "/eva/images/hs/emergency-exit.jpg",
        narration:
            "Our first safety topic is emergency escape routes. " +
            "Corridors, stairways and emergency exits must remain " +
            "clear. Even temporary storage can delay evacuation " +
            "when every second counts.",
        description:
            "Boxes, furniture and other obstructions can delay " +
            "evacuation and prevent people leaving safely.",
        action:
            "Keep designated escape routes clear and report " +
            "obstructions immediately."
    },
    {
        id: "slips",
        title: "Slips, Trips & Falls",
        image: "/eva/images/hs/slips-trips.jpg",
        narration:
            "Slips, trips and falls are common workplace hazards. " +
            "Look out for wet floors, trailing cables, damaged " +
            "floor coverings and objects left in walkways.",
        description:
            "Wet surfaces, loose cables and uneven flooring " +
            "can lead to serious injuries.",
        action:
            "Report hazards promptly and use appropriate " +
            "warning signs until they are made safe."
    },
    {
        id: "firedoors",
        title: "Fire Doors",
        image: "/eva/images/hs/fire-doors.jpg",
        narration:
            "Fire doors help limit the spread of fire and smoke. " +
            "They must not be wedged open unless held by an " +
            "approved automatic release system. Damaged doors " +
            "and faulty closing mechanisms must be reported.",
        description:
            "A wedged-open or defective fire door may fail " +
            "to provide its intended protection.",
        action:
            "Never wedge fire doors open. Report damage " +
            "or doors that fail to close correctly."
    },
    {
        id: "electrical",
        title: "Electrical Safety",
        image: "/eva/images/hs/electrical.jpg",
        narration:
            "Electrical safety is everyone's responsibility. " +
            "Damaged cables, overloaded extension leads and " +
            "unauthorised temporary wiring can cause electric " +
            "shock or fire.",
        description:
            "Damaged electrical equipment and overloaded " +
            "sockets can create shock and fire risks.",
        action:
            "Stop using damaged equipment and report it. " +
            "Do not attempt electrical repairs unless authorised."
    },
    {
        id: "manual",
        title: "Manual Handling",
        image: "/eva/images/hs/manual-handling.jpg",
        narration:
            "Lifting and moving heavy or awkward objects can " +
            "cause musculoskeletal injuries. Consider the load, " +
            "your surroundings and whether mechanical assistance " +
            "or help from a colleague is needed.",
        description:
            "Unsafe lifting and moving techniques can " +
            "cause back injuries and other strains.",
        action:
            "Assess the task, follow your manual handling " +
            "training and use suitable assistance."
    },
    {
        id: "height",
        title: "Working at Height",
        image: "/eva/images/hs/working-at-height.jpg",
        narration:
            "Working at height must be properly planned and " +
            "controlled. Never stand on an office chair or " +
            "improvise access equipment to reach high shelves " +
            "or fittings.",
        description:
            "Standing on chairs or using unsuitable access " +
            "equipment can result in serious falls.",
        action:
            "Use suitable access equipment and follow " +
            "the site's work-at-height procedures."
    },
    {
        id: "equipment",
        title: "Fire Safety Equipment",
        image: "/eva/images/hs/fire-equipment.jpg",
        narration:
            "Fire extinguishers, alarm call points and other " +
            "emergency equipment must remain visible and " +
            "accessible. Never obstruct them with furniture, " +
            "deliveries or stored materials.",
        description:
            "Obstructed fire equipment may delay the response " +
            "to an emergency.",
        action:
            "Keep equipment accessible and report missing, " +
            "damaged or obstructed safety equipment."
    },
    {
        id: "plant",
        title: "Plant & Machinery",
        image: "/eva/images/hs/plant-machinery.jpg",
        narration:
            "Plant rooms and mechanical equipment can contain " +
            "moving machinery, hot surfaces and electrical " +
            "hazards. Access must be controlled and maintenance " +
            "must be undertaken by authorised personnel.",
        description:
            "Mechanical plant may present moving-part, " +
            "heat, electrical and stored-energy hazards.",
        action:
            "Respect restricted access areas and never " +
            "interfere with plant or machinery."
    },
    {
        id: "substances",
        title: "Hazardous Substances",
        image: "/eva/images/hs/hazardous-substances.jpg",
        narration:
            "Cleaning chemicals and other hazardous substances " +
            "must be stored, labelled and used correctly. " +
            "Follow the relevant safety instructions and " +
            "never transfer chemicals into unlabelled containers.",
        description:
            "Incorrect handling or storage of chemicals " +
            "can cause injury or harmful exposure.",
        action:
            "Follow the relevant COSHH controls and " +
            "report spills or damaged containers."
    },
    {
        id: "emergency",
        title: "Emergency Procedures & First Aid",
        image: "/eva/images/hs/first-aid.jpg",
        narration:
            "Finally, make sure you understand the site's " +
            "emergency arrangements. Know how to raise the " +
            "alarm, where to assemble and how to contact " +
            "a first aider. Always follow the building's " +
            "specific emergency procedures.",
        description:
            "A delayed or confused response can make " +
            "an emergency more serious.",
        action:
            "Know the site's alarm arrangements, assembly " +
            "point and first-aid contacts."
    }
];


//========================================================
// Speech Timing
//
// Assumes mono 16-bit PCM at 16000 Hz.
//
// EvaVideo.speak() should return audioBytes.length.
// If it doesn't, we estimate a conservative duration.
//
// This is demo timing, not a guaranteed playback-ended
// notification from Simli.
//========================================================

const PCM_BYTES_PER_SECOND = 16000 * 2;
const AFTER_SPEECH_PAUSE_MS = 1400;
const BETWEEN_TOPICS_PAUSE_MS = 1000;


//========================================================
// Evidence Photograph
//========================================================

function EvidencePhotograph({ topic }) {

    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setFailed(false);
    }, [topic.id]);

    if (failed) {
        return (
            <div className="eva-hs-photo-placeholder">
                <span>EXAMPLE SAFETY EVIDENCE</span>
                <strong>{topic.title}</strong>
                <small>
                    Add photograph:
                    {" "}
                    {topic.image.split("/").pop()}
                </small>
            </div>
        );
    }

    return (
        <img
            src={topic.image}
            alt={`Illustrative example: ${topic.title}`}
            onError={() => setFailed(true)}
        />
    );
}


//========================================================
// Briefing Component
//========================================================

export default function EvaHealthSafetyBriefing({
    onClose
}) {

    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [phase, setPhase] = useState("intro");
    const [ready, setReady] = useState(false);
    const [topicIndex, setTopicIndex] = useState(0);
    const [status, setStatus] = useState(
        "Preparing Fiona"
    );

    //----------------------------------------------------
    // Refs
    //----------------------------------------------------

    const videoRef = useRef(null);
    const timerRef = useRef(null);
    const runIdRef = useRef(0);
    const readyRef = useRef(false);
    const phaseRef = useRef("intro");

    //----------------------------------------------------
    // State Helper
    //----------------------------------------------------

    const changePhase = useCallback(next => {

        phaseRef.current = next;
        setPhase(next);

    }, []);

    //----------------------------------------------------
    // Timer Management
    //----------------------------------------------------

    const clearTimer = useCallback(() => {

        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

    }, []);

    //----------------------------------------------------
    // Cleanup
    //----------------------------------------------------

    useEffect(() => {

        return () => {
            runIdRef.current += 1;
            clearTimer();
        };

    }, [clearTimer]);

    //----------------------------------------------------
    // Simli Ready
    //----------------------------------------------------

    const handleReady = useCallback(() => {

        readyRef.current = true;
        setReady(true);
        setStatus("Fiona is ready");

    }, []);

    //----------------------------------------------------
    // Close
    //----------------------------------------------------

    const closeBriefing = () => {

        runIdRef.current += 1;
        clearTimer();

        if (onClose) {
            onClose();
        }

    };

    //----------------------------------------------------
    // Play Topic
    //----------------------------------------------------

    const playTopic = useCallback(async index => {

        if (index >= HEALTH_SAFETY_TOPICS.length) {

            changePhase("complete");
            setStatus("Briefing complete");
            return;

        }

        const runId = ++runIdRef.current;
        const topic = HEALTH_SAFETY_TOPICS[index];

        clearTimer();

        setTopicIndex(index);
        changePhase("speaking");

        setStatus(
            `Fiona is presenting topic ${index + 1}`
        );

        try {

            //------------------------------------------------
            // Speak Topic
            //------------------------------------------------

            const result = await videoRef.current.speak(
                topic.narration
            );

            if (runId !== runIdRef.current) {
                return;
            }

            //------------------------------------------------
            // Calculate Approximate Audio Duration
            //------------------------------------------------

            const words =
                topic.narration.trim().split(/\s+/).length;

            const estimatedDuration =
                typeof result === "number"
                    ? result / PCM_BYTES_PER_SECOND * 1000
                    : words / 120 * 60000;

            //------------------------------------------------
            // Show Evidence After Narration
            //
            // EvaVideo.speak() returns when audio has
            // been sent, not when playback finishes.
            //
            // We therefore wait approximately the
            // audio duration plus a short pause.
            //------------------------------------------------

            timerRef.current = setTimeout(() => {

                if (runId !== runIdRef.current) {
                    return;
                }

                changePhase("evidence");
                setStatus("Awaiting acknowledgement");

            }, Math.max(2000, estimatedDuration) +
               AFTER_SPEECH_PAUSE_MS);

        }
        catch (error) {

            console.error(
                "H&S briefing speech failed:",
                error
            );

            if (runId !== runIdRef.current) {
                return;
            }

            setStatus("Speech unavailable");

            // Keep the demonstration usable even if
            // the speech service is unavailable.

            changePhase("evidence");

        }

    }, [changePhase, clearTimer]);

    //----------------------------------------------------
    // Start Briefing
    //----------------------------------------------------

    const startBriefing = () => {

        if (!readyRef.current) {
            return;
        }

        playTopic(0);

    };

    //----------------------------------------------------
    // Acknowledge Evidence
    //----------------------------------------------------

    const acknowledgeEvidence = () => {

        if (phaseRef.current !== "evidence") {
            return;
        }

        const nextIndex = topicIndex + 1;

        clearTimer();

        if (nextIndex >= HEALTH_SAFETY_TOPICS.length) {

            changePhase("complete");
            setStatus("Briefing complete");
            return;

        }

        changePhase("waiting");
        setStatus("Preparing next topic");

        timerRef.current = setTimeout(() => {

            playTopic(nextIndex);

        }, BETWEEN_TOPICS_PAUSE_MS);

    };

    //----------------------------------------------------
    // Restart
    //----------------------------------------------------

    const restartBriefing = () => {

        runIdRef.current += 1;
        clearTimer();

        setTopicIndex(0);
        changePhase("intro");
        setStatus("Fiona is ready");

    };

    //----------------------------------------------------
    // Current Topic
    //----------------------------------------------------

    const currentTopic =
        HEALTH_SAFETY_TOPICS[topicIndex];

    const progress =
        Math.round(
            (
                topicIndex /
                HEALTH_SAFETY_TOPICS.length
            ) * 100
        );

    //----------------------------------------------------
    // Render
    //----------------------------------------------------

    return (

        <div className="eva-hs-root">

            {/*===============================================
              White Briefing Drawer
            ===============================================*/}

            <aside className="eva-hs-drawer">

                <header className="eva-hs-header">

                    <div>
                        <small>OMNISCAN PRO / EVA</small>
                        <strong>SAFETY BRIEFING</strong>
                    </div>

                    <button
                        type="button"
                        onClick={closeBriefing}
                        aria-label="Close briefing"
                    >
                        ×
                    </button>

                </header>

                <div className="eva-hs-drawer-content">

                    {/*=======================================
                      Fiona Avatar
                    =======================================*/}

                    <div className="eva-hs-fiona">

                        <EvaVideo
                            ref={videoRef}
                            visible={true}
                            onReady={handleReady}
                        />

                    </div>

                    <div className="eva-hs-fiona-name">
                        <strong>FIONA</strong>
                        <span>
                            Virtual Health & Safety Guide
                        </span>
                    </div>

                    {/*=======================================
                      Introduction
                    =======================================*/}

                    {phase === "intro" && (

                        <section className="eva-hs-section">

                            <div className="eva-hs-eyebrow">
                                WORKPLACE INDUCTION
                            </div>

                            <h2>
                                Health & Safety
                                <br />
                                Briefing
                            </h2>

                            <p>
                                Welcome to your interactive
                                workplace safety briefing.
                            </p>

                            <p>
                                Fiona will introduce ten
                                common workplace hazards,
                                explain the risks and show
                                you photographic examples.
                            </p>

                            <p>
                                Please review each example
                                and click <strong>OK</strong>
                                {" "}to continue to the
                                next topic.
                            </p>

                            <div className="eva-hs-info">
                                <strong>10 safety topics</strong>
                                <span>
                                    Guided explanation
                                    and visual examples
                                </span>
                            </div>

                            <div className="eva-hs-notice">
                                Demonstration briefing.
                                Actual workplace inductions
                                must be tailored to the
                                site and its assessed risks.
                            </div>

                        </section>

                    )}

                    {/*=======================================
                      Active Briefing
                    =======================================*/}

                    {[
                        "speaking",
                        "evidence",
                        "waiting"
                    ].includes(phase) && (

                        <section className="eva-hs-section">

                            <div className="eva-hs-eyebrow">
                                BRIEFING IN PROGRESS
                            </div>

                            <div className="eva-hs-progress-label">

                                <strong>
                                    Topic {topicIndex + 1}
                                    {" "}of{" "}
                                    {HEALTH_SAFETY_TOPICS.length}
                                </strong>

                                <span>
                                    {progress}% reviewed
                                </span>

                            </div>

                            <div className="eva-hs-progress">
                                <div
                                    style={{
                                        width: `${progress}%`
                                    }}
                                />
                            </div>

                            <h2>{currentTopic.title}</h2>

                            <p>
                                {currentTopic.description}
                            </p>

                            <div className="eva-hs-speaking-status">

                                <span
                                    className={
                                        phase === "speaking"
                                            ? "eva-hs-status-dot active"
                                            : "eva-hs-status-dot"
                                    }
                                />

                                {phase === "speaking"
                                    ? "Fiona is explaining this hazard"
                                    : phase === "evidence"
                                        ? "Review the example and click OK"
                                        : "Moving to the next topic"}

                            </div>

                            <div className="eva-hs-info">
                                <strong>Safety reminder</strong>
                                <span>{currentTopic.action}</span>
                            </div>

                        </section>

                    )}

                    {/*=======================================
                      Completion
                    =======================================*/}

                    {phase === "complete" && (

                        <section className="eva-hs-section">

                            <div className="eva-hs-complete-symbol">
                                ✓
                            </div>

                            <div className="eva-hs-eyebrow">
                                BRIEFING COMPLETE
                            </div>

                            <h2>
                                Well done!
                            </h2>

                            <p>
                                You've reviewed all ten
                                example workplace health
                                and safety topics.
                            </p>

                            <div className="eva-hs-info">
                                <strong>10 of 10 topics reviewed</strong>
                                <span>
                                    Ready for the optional
                                    knowledge questionnaire.
                                </span>
                            </div>

                            <div className="eva-hs-notice">
                                This is a demonstration
                                completion only. It is not
                                a certified induction record.
                            </div>

                        </section>

                    )}

                </div>

                {/*===========================================
                  Drawer Footer
                ===========================================*/}

                <footer className="eva-hs-footer">

                    <div className="eva-hs-footer-status">
                        {status}
                    </div>

                    {phase === "intro" && (

                        <button
                            type="button"
                            className="eva-hs-primary"
                            disabled={!ready}
                            onClick={startBriefing}
                        >
                            {ready
                                ? "Start Briefing →"
                                : "Connecting to Fiona..."}
                        </button>

                    )}

                    {phase === "complete" && (

                        <>
                            <button
                                type="button"
                                className="eva-hs-primary"
                                onClick={closeBriefing}
                            >
                                Finish Briefing
                            </button>

                            <button
                                type="button"
                                className="eva-hs-secondary"
                                onClick={restartBriefing}
                            >
                                Restart
                            </button>
                        </>

                    )}

                </footer>

            </aside>

            {/*===============================================
              Evidence Popup
            ===============================================*/}

            {phase === "evidence" && (

                <div
                    className={
                        "eva-hs-evidence " +
                        `eva-hs-position-${topicIndex % 3}`
                    }
                    role="dialog"
                    aria-modal="true"
                    aria-label={currentTopic.title}
                >

                    <div className="eva-hs-evidence-header">

                        <div>
                            <small>
                                EXAMPLE HAZARD
                                {" / "}
                                {String(topicIndex + 1).padStart(2, "0")}
                            </small>

                            <strong>
                                {currentTopic.title}
                            </strong>
                        </div>

                    </div>

                    <div className="eva-hs-evidence-image">
                        <EvidencePhotograph
                            topic={currentTopic}
                        />
                    </div>

                    <div className="eva-hs-evidence-body">

                        <p>{currentTopic.description}</p>

                        <div className="eva-hs-evidence-action">
                            <strong>Recommended action</strong>
                            <span>{currentTopic.action}</span>
                        </div>

                    </div>

                    <div className="eva-hs-evidence-footer">

                        <span>
                            {topicIndex + 1}
                            {" / "}
                            {HEALTH_SAFETY_TOPICS.length}
                        </span>

                        <button
                            type="button"
                            onClick={acknowledgeEvidence}
                        >
                            OK — Continue →
                        </button>

                    </div>

                </div>

            )}

        </div>

    );

}
